'use client';

import { KeyboardEvent, useEffect, useRef, useState } from 'react';

import { ArrowDown, ArrowRight, Bot, Film, Globe2, Instagram, Music2, PackageCheck, Rocket, Send, Youtube } from 'lucide-react';
import Link from 'next/link';

import type { PricingCopy } from '@/libs/pricing-i18n';
import type { StripePlan } from '@/types/config';

import AiChatPricing from './AiChatPricing';
import PricingCard from './PricingCard';

function DRDisplay({ copy }: { copy: PricingCopy['submit'] }) {
  const [drValue, setDrValue] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/config/dr')
      .then((response) => response.json())
      .then((data) => setDrValue(data.dr == null ? null : String(data.dr)))
      .catch(() => setDrValue(null));
  }, []);

  return (
    <div className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 border-l-2 border-[color:var(--editorial-accent)] pl-4 text-sm text-[color:var(--editorial-muted)]">
      <span className="whitespace-nowrap">{copy.authority}</span>
      <span className="price-figures whitespace-nowrap font-semibold text-[color:var(--editorial-ink)]">
        {drValue ? `DR ${drValue}` : copy.establishedDomain}
      </span>
      {drValue && (
        <a
          href="https://ahrefs.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full text-xs underline decoration-[color:var(--editorial-line)] underline-offset-4 hover:text-[color:var(--editorial-ink)] sm:w-auto"
        >
          {copy.ahrefsRating}
        </a>
      )}
    </div>
  );
}

type Props = {
  plans: StripePlan[];
  locale: string;
  copy: PricingCopy;
  aiChatPlusMonthlyPriceUsd: number;
  aiChatProMonthlyPriceUsd: number;
};

type PricingTab = 'chat' | 'submit';

function pricingTabFromHash(hash: string): PricingTab {
  return hash === '#submit-pricing' ? 'submit' : 'chat';
}

function scrollToPricingTabs() {
  window.requestAnimationFrame(() => {
    document.getElementById('pricing-tabs')?.scrollIntoView({ block: 'start' });
  });
}

export default function Pricing({
  plans,
  locale,
  copy,
  aiChatPlusMonthlyPriceUsd,
  aiChatProMonthlyPriceUsd,
}: Props) {
  const [activeTab, setActiveTab] = useState<PricingTab>('chat');
  const tabRefs = useRef<Record<PricingTab, HTMLButtonElement | null>>({ chat: null, submit: null });
  const videoPlan = plans.find((plan) => plan.planCode === 'video_launch');
  const deliveryIcons = [Globe2, PackageCheck, Film, Send];
  const activeDeliverySteps = (videoPlan ? copy.delivery.steps : copy.delivery.steps.slice(0, 2)).map((step, index) => ({
    ...step,
    icon: deliveryIcons[index],
  }));
  const faqItems = videoPlan ? copy.faq.video : copy.faq.listing;
  const videosHref = locale === 'en' ? '/videos' : `/${locale}/videos`;
  const liveChannels = [
    { name: 'YouTube', detail: copy.distribution.youtubeDetail, icon: Youtube },
    { name: 'X', detail: copy.distribution.xDetail, icon: null },
  ];
  const upcomingChannels = [
    { name: 'Instagram', detail: copy.distribution.instagramDetail, icon: Instagram },
    { name: 'TikTok', detail: copy.distribution.tiktokDetail, icon: Music2 },
  ];

  function selectTab(tab: PricingTab, updateUrl = true) {
    setActiveTab(tab);
    if (!updateUrl) return;
    const hash = tab === 'chat' ? '#ai-chat-plans' : '#submit-pricing';
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
    scrollToPricingTabs();
  }

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, tab: PricingTab) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextTab: PricingTab = event.key === 'Home'
      ? 'chat'
      : event.key === 'End'
        ? 'submit'
        : tab === 'chat' ? 'submit' : 'chat';
    selectTab(nextTab);
    tabRefs.current[nextTab]?.focus();
  }

  useEffect(() => {
    const syncTabFromHash = () => {
      const nextTab = pricingTabFromHash(window.location.hash);
      setActiveTab(nextTab);
      if (window.location.hash) scrollToPricingTabs();
    };
    syncTabFromHash();
    window.addEventListener('hashchange', syncTabFromHash);
    return () => window.removeEventListener('hashchange', syncTabFromHash);
  }, []);

  return (
    <section className="editorial-page overflow-hidden" id="pricing">
      <div className="mx-auto max-w-7xl px-5 pb-24 pt-6 sm:px-8 sm:pt-8 lg:pb-28">
        <nav id="pricing-tabs" className="scroll-mt-28" aria-label={copy.page.sectionsLabel}>
          <div className="grid grid-cols-2 gap-2 rounded-[22px] border border-[color:var(--editorial-line)] bg-white/75 p-2 shadow-[0_14px_42px_rgba(16,35,63,.06)]" role="tablist">
            <button
              ref={(node) => { tabRefs.current.chat = node; }}
              id="pricing-tab-chat"
              type="button"
              role="tab"
              aria-selected={activeTab === 'chat'}
              aria-controls="pricing-panel-chat"
              tabIndex={activeTab === 'chat' ? 0 : -1}
              onClick={() => selectTab('chat')}
              onKeyDown={(event) => handleTabKeyDown(event, 'chat')}
              className={`grid min-h-[64px] grid-cols-[34px_minmax(0,1fr)] items-center gap-2 rounded-2xl border px-3 text-left transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--editorial-accent)] focus-visible:ring-offset-2 sm:min-h-[82px] sm:grid-cols-[44px_minmax(0,1fr)] sm:gap-4 sm:px-4 ${activeTab === 'chat' ? 'border-[color:var(--editorial-accent)] bg-[color:var(--editorial-accent-dark)] text-white shadow-[0_14px_30px_rgba(37,83,205,.2)]' : 'border-transparent bg-transparent text-[color:var(--editorial-ink)] hover:-translate-y-0.5 hover:border-[color:var(--editorial-line)] hover:bg-white'}`}
            >
              <span className={`flex h-9 w-9 items-center justify-center rounded-lg sm:h-11 sm:w-11 sm:rounded-xl ${activeTab === 'chat' ? 'bg-white/[0.15] text-white' : 'bg-[color:var(--editorial-accent-soft)] text-[color:var(--editorial-accent-dark)]'}`} aria-hidden="true"><Bot className="h-4 w-4 sm:h-5 sm:w-5" /></span>
              <span className="min-w-0">
                <strong className="block text-sm font-semibold sm:text-base">{copy.navigation.chatTitle}</strong>
                <small className={`mt-1 hidden text-sm sm:block ${activeTab === 'chat' ? 'text-white/75' : 'text-[color:var(--editorial-muted)]'}`}>{copy.navigation.chatHint}</small>
              </span>
            </button>
            <button
              ref={(node) => { tabRefs.current.submit = node; }}
              id="pricing-tab-submit"
              type="button"
              role="tab"
              aria-selected={activeTab === 'submit'}
              aria-controls="pricing-panel-submit"
              tabIndex={activeTab === 'submit' ? 0 : -1}
              onClick={() => selectTab('submit')}
              onKeyDown={(event) => handleTabKeyDown(event, 'submit')}
              className={`grid min-h-[64px] grid-cols-[34px_minmax(0,1fr)] items-center gap-2 rounded-2xl border px-3 text-left transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--editorial-accent)] focus-visible:ring-offset-2 sm:min-h-[82px] sm:grid-cols-[44px_minmax(0,1fr)] sm:gap-4 sm:px-4 ${activeTab === 'submit' ? 'border-[color:var(--editorial-ink)] bg-[color:var(--editorial-ink)] text-white shadow-[0_14px_30px_rgba(16,35,63,.18)]' : 'border-transparent bg-transparent text-[color:var(--editorial-ink)] hover:-translate-y-0.5 hover:border-[color:var(--editorial-line)] hover:bg-white'}`}
            >
              <span className={`flex h-9 w-9 items-center justify-center rounded-lg sm:h-11 sm:w-11 sm:rounded-xl ${activeTab === 'submit' ? 'bg-white/[0.12] text-white' : 'bg-[color:var(--editorial-ink)] text-white'}`} aria-hidden="true"><Rocket className="h-4 w-4 sm:h-5 sm:w-5" /></span>
              <span className="min-w-0">
                <strong className="block text-sm font-semibold sm:text-base">{copy.navigation.launchTitle}</strong>
                <small className={`mt-1 hidden text-sm sm:block ${activeTab === 'submit' ? 'text-white/70' : 'text-[color:var(--editorial-muted)]'}`}>{copy.navigation.launchHint}</small>
              </span>
            </button>
          </div>
        </nav>

        <div
          id="pricing-panel-chat"
          role="tabpanel"
          aria-labelledby="pricing-tab-chat"
          hidden={activeTab !== 'chat'}
        >
          <AiChatPricing
            locale={locale}
            pricingCopy={copy.chat}
            plusMonthlyPriceUsd={aiChatPlusMonthlyPriceUsd}
            proMonthlyPriceUsd={aiChatProMonthlyPriceUsd}
          />
        </div>

        <div
          id="pricing-panel-submit"
          role="tabpanel"
          aria-labelledby="pricing-tab-submit"
          hidden={activeTab !== 'submit'}
        >
        <section id="submit-pricing" className="scroll-mt-28 pt-14 sm:pt-16" aria-labelledby="submit-pricing-heading">
          <div className="grid gap-6 border-b border-[color:var(--editorial-line)] pb-7 lg:grid-cols-[minmax(0,1fr)_minmax(280px,.55fr)] lg:items-end">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--editorial-accent-dark)]">{copy.submit.kicker}</p>
                <span className="rounded-full border border-[color:var(--editorial-line)] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--editorial-muted)]">{copy.submit.oneTimePayment}</span>
              </div>
              <h1 id="submit-pricing-heading" className="font-editorial mt-3 text-4xl font-semibold tracking-[-0.035em] text-[color:var(--editorial-ink)] sm:text-5xl">
                {copy.submit.title}
              </h1>
            </div>
            <div className="space-y-4 lg:justify-self-end">
              <p className="max-w-[46ch] text-sm leading-6 text-[color:var(--editorial-muted)]">{copy.submit.intro}</p>
              <DRDisplay copy={copy.submit} />
            </div>
          </div>

          <div className={videoPlan ? 'mt-8 grid items-stretch gap-6 lg:grid-cols-[0.88fr_1.12fr] lg:gap-8' : 'mx-auto mt-8 grid max-w-2xl items-stretch gap-6'}>
          {plans.map((plan) => (
            <PricingCard key={plan.planCode} plan={plan} locale={locale} copy={copy.submit} />
          ))}
          </div>
        </section>

        {videoPlan && (
          <section
            className="mt-20 overflow-hidden border-y border-[color:var(--editorial-ink)] bg-white/70 lg:grid lg:grid-cols-[minmax(0,.78fr)_minmax(0,1.22fr)]"
            aria-labelledby="distribution-heading"
          >
            <div className="border-b border-[color:var(--editorial-line)] p-7 sm:p-9 lg:border-b-0 lg:border-r lg:p-10">
              <p className="price-figures text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--editorial-accent-dark)]">
                {copy.distribution.liveCount}
              </p>
              <h2 id="distribution-heading" className="font-editorial mt-4 max-w-md text-4xl font-semibold leading-[1.02] tracking-[-0.035em] text-[color:var(--editorial-ink)] sm:text-5xl">
                {copy.distribution.title}
              </h2>
              <p className="mt-5 max-w-[44ch] text-sm leading-6 text-[color:var(--editorial-muted)] sm:text-base sm:leading-7">
                {copy.distribution.intro}
              </p>
              <Link
                href={videosHref}
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--editorial-accent-dark)] underline decoration-[color:var(--editorial-accent)] underline-offset-4 transition-colors hover:text-[color:var(--editorial-ink)]"
              >
                {copy.distribution.watchExamples}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>

            <div className="grid min-w-0 sm:grid-cols-[1.08fr_.92fr]">
              <div className="border-b border-[color:var(--editorial-line)] p-7 sm:border-b-0 sm:border-r sm:p-9">
                <div className="flex items-center justify-between gap-4 border-b border-[color:var(--editorial-line)] pb-4">
                  <h3 className="text-sm font-semibold text-[color:var(--editorial-ink)]">{copy.distribution.publishingNow}</h3>
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--editorial-success)]">{copy.distribution.included}</span>
                </div>
                <ul className="divide-y divide-[color:var(--editorial-line)]" aria-label={copy.distribution.currentChannels}>
                  {liveChannels.map((channel) => {
                    const Icon = channel.icon;
                    return (
                      <li key={channel.name} className="grid grid-cols-[44px_minmax(0,1fr)] gap-4 py-6">
                        <span className="flex h-11 w-11 items-center justify-center rounded-[12px] bg-[color:var(--editorial-ink)] text-white" aria-hidden="true">
                          {Icon ? <Icon className="h-5 w-5" /> : <span className="text-lg font-semibold leading-none">X</span>}
                        </span>
                        <div className="min-w-0">
                          <p className="text-lg font-semibold text-[color:var(--editorial-ink)]">{channel.name}</p>
                          <p className="mt-1 text-sm leading-6 text-[color:var(--editorial-muted)]">{channel.detail}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="bg-[color:var(--editorial-paper)] p-7 sm:p-9">
                <div className="flex items-center justify-between gap-4 border-b border-[color:var(--editorial-line)] pb-4">
                  <h3 className="text-sm font-semibold text-[color:var(--editorial-ink)]">{copy.distribution.comingNext}</h3>
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--editorial-muted)]">{copy.distribution.roadmap}</span>
                </div>
                <ul className="divide-y divide-[color:var(--editorial-line)]" aria-label={copy.distribution.plannedChannels}>
                  {upcomingChannels.map((channel) => {
                    const Icon = channel.icon;
                    return (
                      <li key={channel.name} className="grid grid-cols-[44px_minmax(0,1fr)] gap-4 py-6">
                        <span className="flex h-11 w-11 items-center justify-center rounded-[12px] border border-[color:var(--editorial-line)] bg-white text-[color:var(--editorial-muted)]" aria-hidden="true">
                          <Icon className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-lg font-semibold text-[color:var(--editorial-ink)]">{channel.name}</p>
                          <p className="mt-1 text-sm leading-6 text-[color:var(--editorial-muted)]">{channel.detail}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </section>
        )}

        <section className="mt-24" aria-labelledby="delivery-heading">
          <div className="flex flex-col justify-between gap-4 border-b border-[color:var(--editorial-line)] pb-6 sm:flex-row sm:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--editorial-accent-dark)]">{copy.delivery.kicker}</p>
              <h2 id="delivery-heading" className="font-editorial mt-3 text-4xl font-semibold tracking-[-0.03em] text-[color:var(--editorial-ink)] sm:text-5xl">
                {videoPlan ? copy.delivery.videoTitle : copy.delivery.listingTitle}
              </h2>
            </div>
            <ArrowDown className="hidden h-7 w-7 text-[color:var(--editorial-accent)] sm:block" aria-hidden="true" />
          </div>
          <ol className={videoPlan ? 'grid divide-y divide-[color:var(--editorial-line)] lg:grid-cols-4 lg:divide-x lg:divide-y-0' : 'grid divide-y divide-[color:var(--editorial-line)] sm:grid-cols-2 sm:divide-x sm:divide-y-0'}>
            {activeDeliverySteps.map(({ icon: Icon, title, description }, index) => (
              <li key={title} className="px-0 py-7 lg:px-6 lg:first:pl-0 lg:last:pr-0">
                <div className="flex items-center gap-3">
                  <span className="price-figures text-xs font-semibold text-[color:var(--editorial-accent-dark)]">0{index + 1}</span>
                  <Icon className="h-4 w-4 text-[color:var(--editorial-ink)]" aria-hidden="true" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-[color:var(--editorial-ink)]">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-[color:var(--editorial-muted)]">{description}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-20 grid gap-10 border-t border-[color:var(--editorial-ink)] pt-10 lg:grid-cols-[.7fr_1.3fr]" aria-labelledby="pricing-faq-heading">
          <div>
            <h2 id="pricing-faq-heading" className="font-editorial text-4xl font-semibold tracking-[-0.03em] text-[color:var(--editorial-ink)]">{copy.faq.title}</h2>
            <p className="mt-3 max-w-sm text-sm leading-6 text-[color:var(--editorial-muted)]">
              {videoPlan ? copy.faq.videoIntro : copy.faq.listingIntro}
            </p>
          </div>
          <div className="divide-y divide-[color:var(--editorial-line)] border-y border-[color:var(--editorial-line)]">
            {faqItems.map(([question, answer]) => (
              <details key={question} className="group py-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-base font-semibold text-[color:var(--editorial-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--editorial-accent)]">
                  {question}
                  <span className="text-xl font-normal transition-transform duration-200 group-open:rotate-45" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 max-w-2xl pr-10 text-sm leading-6 text-[color:var(--editorial-muted)]">{answer}</p>
              </details>
            ))}
          </div>
        </section>
        </div>
      </div>

    </section>
  );
}
